import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { primitiveColors } from '@/theme/tokens';

interface MinimalThinkingStateProps {
  size?: number;
}

export const MinimalThinkingState: React.FC<MinimalThinkingStateProps> = ({ size = 32 }) => {
  const [animationState, setAnimationState] = useState(0); // 0, 1, 2, 3
  const scale = size / 46; // Base size is 46px from your SVG

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    const startAnimation = () => {
      interval = setInterval(() => {
        setAnimationState((prev) => {
          if (prev === 3) {
            // Clear interval and set timeout for the hold period
            clearInterval(interval);
            timeout = setTimeout(() => {
              setAnimationState(0);
              startAnimation(); // Restart the cycle
            }, 500);
            return 3;
          }
          return prev + 1;
        });
      }, 250);
    };

    startAnimation();

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Define opacity values for each state
  const getOpacity = (part: 'blue' | 'red' | 'purple') => {
    switch (animationState) {
      case 0: // 0% - all at 30%
        return 0.3;
      case 1: // 33% - blue full, others 50%
        return part === 'blue' ? 1 : 0.5;
      case 2: // 66% - blue full, red 50%, purple full
        if (part === 'blue') return 1;
        if (part === 'red') return 0.5;
        return 1; // purple
      case 3: // 100% - all full
        return 1;
      default:
        return 0.3;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        transform: `scale(${scale})`,
        transformOrigin: 'left center',
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      <svg width="46" height="40" viewBox="0 0 46 40" fill="none">
        <path
          d="M0 13.4971L10.152 40.0001L20.1227 13.4971H0Z"
          fill={primitiveColors.blue7}
          style={{ opacity: getOpacity('blue'), transition: 'opacity 0.2s ease-in-out' }}
        />
        <path
          d="M19.8779 13.3807L28.9051 37.7914C30.7105 36.5257 32.1549 34.5367 32.8771 32.3668L45.154 0H33.2381C28.1829 0 23.8499 3.07395 22.0444 7.77527L20.0585 13.3807"
          fill={primitiveColors.red4}
          style={{ opacity: getOpacity('red'), transition: 'opacity 0.2s ease-in-out' }}
        />
        <path
          d="M28.9572 37.6504C26.9777 39.0963 24.4583 39.9999 21.7589 39.9999H10.0615L19.9593 13.2515L28.9572 37.6504Z"
          fill={primitiveColors.logoPurple}
          style={{ opacity: getOpacity('purple'), transition: 'opacity 0.2s ease-in-out' }}
        />
      </svg>
    </Box>
  );
};
